import { Box, Button, FormControl, FormHelperText, Grid, IconButton, OutlinedInput, Stack, Tooltip, Typography, useMediaQuery } from '@mui/material'
import { Add, CancelOutlined, EditOutlined, SaveOutlined } from '@mui/icons-material'
import gaiaTheme from '../../../../root-components/theme';
import { useNavigate, useParams } from 'react-router-dom';
import DistributionListService from '../../../../shared/services/distribution-list/distribution-list.service';
import { useContext, useEffect, useRef, useState } from 'react';
import { IDistributionList } from '../../../../shared/services/distribution-list/distribution-list.model';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import DistributionListAddressService from '../../../../shared/services/distribution-list-address/distribution-list-address.service';
import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import { AddDistributionListManuallyDialog } from '../../../dialogs/settings/distribution-list/add-distribution-list-manually-dialog';
import { TableDisplay } from '../../../presentation-components/miscellaneous-components/table-display';
import { TableActionButtons } from '../../../presentation-components/miscellaneous-components/table-action-buttons';
import { SearchBar } from '../../../presentation-components/display-elements-components/search-bar';
import { IDistributionListAddress } from '../../../../shared/services/distribution-list-address/distribution-list-address.model';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { IconName } from '../../../../shared/interfaces/icon-picker.model';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import { useDebounce } from '../../../../hooks/use-debounce-hook';

export const DistributionListDetailPage = () => {
  const { id } = useParams()
  const navigate = useNavigate();
  //Services
  const distributionListService = new DistributionListService()
  const distributionListAddressService = new DistributionListAddressService()
  const userNotificationsContext = useContext(UserNotificationsContext)
  //State
  const [openIndividualDialog, setOpenIndividualDialog] = useState<boolean>(false)
  const [openGenericTeamDialog, setOpenGenericTeamDialog] = useState<boolean>(false)
  const [distributionList, setDistributionList] = useState<IDistributionList>()
  const [distributionListAddresses, setDistributionListAddresses] = useState<IDistributionListAddress[]>()
  const [editField, setEditField] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedUser, setSelectedUser] = useState({
    id: '',
    name: '',
    email: '',
  })
  //Table State
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [change, setChange] = useState<number>(0)
  const [selected, setSelected] = useState<string[]>([]);
  const [orderBy, setOrderBy] = useState<string>()
  const [order, setOrder] = useState<'asc' | 'desc'>()
  //Loading State
  const [hasPageMounted, setHasPageMounted] = useState<boolean>(false)
  //Hooks
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  //Ref
  const requestId = useRef<number>(0)
  //Layout
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

  //Use Effects
  useEffect(() => {
    getDistributionList();
  }, [id])

  useEffect(() => {
    getDistributionListEmailAddresses()
  }, [id, inputPage, perPage, change])

  useEffect(() => {
    if (hasPageMounted) {
      if (inputPage !== 0) {
        setInputPage(0)
      }
      setChange(change + 1)
    }
    setHasPageMounted(true)
  }, [debouncedSearchQuery, order, orderBy])

  const getDistributionList = () => {
    if (id) {
      distributionListService.getDistributionList(id)
        .then(response => {
          setDistributionList(response)
          formik.setValues({
            listName: response.listName
          })
        })
        .catch(err => console.log(err))
    }
  }

  const getDistributionListEmailAddresses = () => {
    if (id) {
      requestId.current++
      const currentRequestId = requestId.current
      distributionListAddressService.getAddressesForDistributionListWithPagination(id, inputPage, perPage, debouncedSearchQuery, orderBy, order)
        .then(response => {
          if (currentRequestId === requestId.current) {
            setDistributionListAddresses(response.rows)
            setTotalRecords(response.count);
          }
        })
        .catch(err => {
          if (currentRequestId === requestId.current) {
            console.log(err)
          }
        })
    }
  }

  //Table Contents
  const headCells: IHeadCell[] = [
    { id: 'action', numeric: false, label: 'Action', sortable: false, includeInMobile: false },
    { id: 'name', numeric: false, label: 'Name', sortable: true, isHeading: true },
    { id: 'emailAddress', numeric: false, label: 'Email', sortable: true },
  ];

  const rows = !distributionListAddresses ? [] : distributionListAddresses.map(email => {

    return {
      id: email.id,
      name: email.name,
      emailAddress: email.emailAddress,
      action: (
        <TableActionButtons
          id={email.id}
          includeEdit={true}
          includeDelete={true}
          onEdit={() => editClickHandler(email.id, email.name, email.emailAddress)}
          onDelete={() => deleteClickHandler(email.id)}
        />
      )
    }
  })

  //Table Event Handlers
  const handleOrderChange = (orderBy: string, order: 'asc' | 'desc',) => {
    setOrderBy(orderBy)
    setOrder(order)
  };

  const editClickHandler = (emailId: string, emailName: string, emailAddress: string) => {
    setOpenIndividualDialog(true)
    setSelectedUser({ id: emailId, name: emailName, email: emailAddress })
  }

  const deleteClickHandler = (emailId: string) => {
    userNotificationsContext.openDialog({ active: true, title: 'Confirmation', message: `Are you sure you want to remove this person from the distribution list?`, buttonCopy: 'Continue', cancelButtonCopy: 'Cancel' })
      .then(response => {
        if (response) {
          distributionListAddressService.deleteDistributionListAddress(emailId)
            .then(response => {
              if (inputPage > 0 && distributionListAddresses && distributionListAddresses.length === 1) {
                setInputPage(inputPage - 1)
              } else {
                getDistributionListEmailAddresses()
              }
              userNotificationsContext.openSnackbar({ open: true, message: 'Deleted successfully' });
            })
            .catch(err => {
              userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
              console.log(err)
            })
        }
      })
  }

  //Formik
  const distributionListSchema = Yup.object().shape({
    listName: Yup.string().required('Name is required'),
  })

  const formik = useFormik({
    initialValues: {
      listName: '',
    },
    validationSchema: distributionListSchema,
    onSubmit: (values) => {
      if (id) {
        distributionListService.editDistributionList(id, values)
          .then(response => {
            getDistributionList();
            userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' });
            setEditField('');
          })
          .catch(err => {
            userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
            console.log(err)
          })
      }
    }
  });

  const toggleEditField = (fieldName: string) => {
    setEditField(fieldName)
  }

  const cancelEditHandler = () => {
    setEditField('')
  }

  const closeManualDialogHandler = () => {
    setOpenIndividualDialog(false)
    setOpenGenericTeamDialog(false)
    toggleEditField('')

  }
  const dialogManualSuccessHandler = () => {
    getDistributionList();
    getDistributionListEmailAddresses()
    setOpenIndividualDialog(false)
    setOpenGenericTeamDialog(false)
    setSelectedUser({
      id: '',
      name: '',
      email: '',
    })
    toggleEditField('')
    userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' });
  }

  const submitDeleteHandler = () => {
    if (id) {
      distributionListService.checkIfDistributionListInUse(id)
        .then(response => {
          if (response === true) {
            userNotificationsContext.openDialog({
              active: true,
              title: 'Action Required',
              message: `You are unable to delete this distribution list as it is currently in use. Please review any associated email surveys and resolve their dependencies before proceeding.`,
              buttonCopy: 'Ok',
              hideCancelButton: true
            })
          } else {
            userNotificationsContext.openDialog({
              active: true,
              title: 'Confirmation',
              message: `Are you sure you want to delete this distribution list? This action cannot be undone.`,
              buttonCopy: 'Continue',
              cancelButtonCopy: 'Cancel'
            })
              .then(response => {
                if (response) {
                  distributionListService.deleteDistributionList(id)
                    .then(response => {
                      userNotificationsContext.openSnackbar({ open: true, message: 'Deleted successfully' });
                      navigate('/settings/company-settings/distribution-lists')
                    }).catch(err => {
                      userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
                      console.log(err)
                    })
                }
              })
          }
        })
    }
  }


  return (
    <PageOuterContainer>
      {distributionList &&
        <>
          <PageHeading
            title={distributionList.listName}
            includeBackButton={true}
            backButtonLink='/settings/company-settings/distribution-lists'
            backButtonText='Back to distribution lists'
            includeActionButton={true}
            actionButtonIcon={IconName.DeleteOutlineOutlined}
            actionButtonHandler={submitDeleteHandler}
            actionButtonText='Delete Distribution List'
            hasTabs={false}
          />
          <Box sx={{ borderWidth: '1px', borderStyle: 'solid', borderColor: 'lightGrey.main', borderRadius: '10px', mb: 4 }}>
            <Box component='form' onSubmit={formik.handleSubmit}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <FormControl fullWidth variant='outlined'>
                <Stack sx={{
                  pt: 1, pb: 1, pl: 3,
                  borderBottomStyle: distributionList.isGenericTeamEmail ? 'solid' : null,
                  borderBottomWidth: distributionList.isGenericTeamEmail ? '1px' : null,
                  borderBottomColor: distributionList.isGenericTeamEmail ? 'lightGrey.main' : null
                }}>
                  <Stack position='relative' alignItems={isMobileMode ? 'flex-start' : 'center'} flexDirection={isMobileMode ? 'column' : 'row'} justifyContent='start'>
                    <Typography variant='subtitle1' sx={{ width: '30%', color: 'text.primary', minWidth: '100px' }}> Name </Typography>
                    <Stack position='relative' flexDirection='column' sx={{ width: '100%' }}>
                      <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'>
                        <OutlinedInput id='listName' size='small' name='listName' value={formik.values.listName} onChange={formik.handleChange} disabled={editField !== 'listName'} fullWidth={!isMobileMode}
                          sx={{
                            mr: 15, mt: 1, mb: 1, ml: isMobileMode ? 0 : 1,
                            ...(editField !== 'listName' && {
                              '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
                            }),
                            ...(editField === 'listName' && {
                              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'text.secondaary' }
                            }),
                            color: 'text.secondary'
                          }}
                          error={formik.touched.listName && Boolean(formik.errors.listName)}
                        ></OutlinedInput>
                        {editField === '' &&
                          <IconButton onClick={() => toggleEditField('listName')} sx={{ mr: 2, position: 'absolute', right: 0 }}>
                            <EditOutlined />
                          </IconButton>}
                        {editField === 'listName' &&
                          <>
                            <IconButton type='submit' sx={{ mr: 2, position: 'absolute', right: 0 }}>
                              <SaveOutlined />
                            </IconButton>
                            <IconButton onClick={cancelEditHandler} sx={{ mr: 2, position: 'absolute', right: 40 }}>
                              <CancelOutlined />
                            </IconButton>
                          </>
                        }
                      </Stack>
                      {formik.touched.listName && formik.errors.listName &&
                        <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.listName}</FormHelperText>
                      }
                    </Stack>
                  </Stack>
                </Stack>
              </FormControl>
              {distributionList.isGenericTeamEmail && <FormControl fullWidth variant='outlined'>
                <Stack sx={{ pt: 1, pb: 1, pl: 3 }}>
                  <Stack position='relative' alignItems={isMobileMode ? 'flex-start' : 'center'} flexDirection={isMobileMode ? 'column' : 'row'} justifyContent='start'>
                    <Typography variant='subtitle1' sx={{ width: '30%', color: 'text.primary', minWidth: '100px' }}> Email Address </Typography>
                    <Stack position='relative' flexDirection='column' sx={{ width: '100%' }}>
                      <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'>
                        <OutlinedInput id='emailAddress' size='small' name='emailAddress'
                          value={distributionList.distributionListAddresses[0].emailAddress}
                          disabled={editField !== 'emailAddress'} fullWidth={!isMobileMode}
                          sx={{
                            mr: 15, mt: 1, mb: 1, ml: isMobileMode ? 0 : 1,
                            ...(editField !== 'emailAddress' && {
                              '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
                            }),
                            color: 'text.secondary'
                          }}
                        ></OutlinedInput>
                        {editField === '' &&
                          <IconButton onClick={() => {
                            toggleEditField('emailAddress')
                            setOpenGenericTeamDialog(true)
                          }
                          } sx={{ mr: 2, position: 'absolute', right: 0 }}>
                            <EditOutlined />
                          </IconButton>}
                      </Stack>
                    </Stack>
                  </Stack>
                </Stack>
              </FormControl>}
            </Box>
            <Box>
            </Box>
          </Box>
          {!distributionList.isGenericTeamEmail && <Box>
            <Grid container justifyContent="space-between" sx={{ mb: 2 }}>
              <Grid
                item
                xl={3} lg={3} md={3} sm={12} xs={12}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  width: '100%'
                }}>
                <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
              </Grid>
              <Grid>
                <Button onClick={() => {
                  setOpenIndividualDialog(true);
                  setSelectedUser({ name: '', email: '', id: '' })
                }} sx={{ mt: isMobileMode ? 2 : 0 }}>
                  <Add />
                  <Typography>Add Contact Address</Typography>
                </Button>
              </Grid>
            </Grid>
            {distributionListAddresses &&
              <TableDisplay
                onOrderChange={handleOrderChange}
                isMobileMode={isMobileMode}
                headCells={headCells}
                rows={rows}
                selected={selected}
                setSelected={setSelected}
                selectByIcon={true}
                inputPage={inputPage}
                setInputPage={setInputPage}
                totalRecords={totalRecords}
                perPage={perPage}
                setPerPage={setPerPage} />}
          </Box>}
        </>}
      {distributionList && !distributionList.isGenericTeamEmail && openIndividualDialog &&
        <AddDistributionListManuallyDialog
          open={openIndividualDialog}
          closeHandler={closeManualDialogHandler}
          successHandler={dialogManualSuccessHandler}
          individualContactsOnly={true}
          distributionListDetails={distributionList}
          selectedUser={selectedUser}
        />}
      {distributionList && distributionList.isGenericTeamEmail && openGenericTeamDialog &&
        <AddDistributionListManuallyDialog
          open={openGenericTeamDialog}
          closeHandler={closeManualDialogHandler}
          successHandler={dialogManualSuccessHandler}
          genericTeamOnly={true}
          distributionListDetails={distributionList}
        />}
    </PageOuterContainer>)
}